import * as React from 'react';
import type { IFormValues } from '../types';

interface UsePreviewPaymentProcessingProps {
	survey?: {
		isPaidEvent?: boolean;
		attendeeOptions?: {
			eventTickets?: {
				id?: string;
				name?: string;
				unitPrice?: number;
			}[];
			costPerAttendee?: number;
		};
	};
	formValues: IFormValues;
}

/**
 * Preview-only payment processing hook.
 * Calculates payment values for preview without actual payment processing.
 * Similar to usePaymentProcessing but without payment submission logic.
 */
export const usePreviewPaymentProcessing = ({ survey, formValues }: UsePreviewPaymentProcessingProps) => {
	// Calculate total tickets order (always unpaid for preview)
	const ticketsOrder = React.useMemo(() => {
		if (!survey?.isPaidEvent || !formValues.isAttending) {
			return 0;
		}

		// For tiered tickets, calculate based on ticket selections
		if (survey?.attendeeOptions?.eventTickets && survey.attendeeOptions.eventTickets.length > 0) {
			const selections = formValues.ticketSelections || {};
			return Object.values(selections).reduce((sum, qty) => sum + qty, 0);
		}

		// Legacy calculation for costPerAttendee
		const isBringingGuest = formValues.bringingGuest;
		const guestCount = formValues.guests?.length || 0;
		let totalTickets = 1; // Always start with 1 for the attendee

		if (!isBringingGuest) {
			return 1;
		}

		if (isBringingGuest && !guestCount) {
			// If bringing guest but no guest count, add 1 for anonymous guest
			totalTickets = totalTickets + 1;
		}

		if (guestCount) {
			// Add all guests to the total
			totalTickets = totalTickets + guestCount;
		}

		return totalTickets;
	}, [
		formValues.bringingGuest,
		formValues.guests?.length,
		formValues.isAttending,
		formValues.ticketSelections,
		survey?.isPaidEvent,
		survey?.attendeeOptions?.eventTickets,
	]);

	// Mock function for paid ticket quantities (always empty for preview)
	const getPaidTicketQuantities = React.useCallback((): Record<string, number> => {
		return {};
	}, []);

	// Calculate formatted amount
	const formattedAmount = React.useMemo(() => {
		// For tiered tickets, calculate total from selections
		if (survey?.attendeeOptions?.eventTickets && survey.attendeeOptions.eventTickets.length > 0) {
			const selections = formValues.ticketSelections || {};
			const tickets = survey.attendeeOptions.eventTickets;

			let totalCents = 0;
			Object.entries(selections).forEach(([ticketId, quantity]) => {
				const ticket = tickets.find(t => t.id && t.id === ticketId);
				if (ticket && quantity > 0) {
					totalCents += (ticket.unitPrice || 0) * quantity;
				}
			});

			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(totalCents / 100);
			return dollarAmount;
		}

		// Legacy calculation for costPerAttendee
		if (survey?.attendeeOptions?.costPerAttendee) {
			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format((survey.attendeeOptions.costPerAttendee * ticketsOrder) / 100);
			return dollarAmount;
		}
		return '$0.00';
	}, [
		survey?.attendeeOptions?.costPerAttendee,
		survey?.attendeeOptions?.eventTickets,
		ticketsOrder,
		formValues.ticketSelections,
	]);

	// Paid tickets is always 0 for preview
	const paidTickets = 0;

	return {
		ticketsOrder,
		formattedAmount,
		paidTickets,
		getPaidTicketQuantities,
	};
};
